import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import { Tabs } from 'radix-ui';
import * as React from 'react';
import { INotesActionsCriteria, RichContentSupportedTypes } from '../../../../models';
import {
	CompanyViewModel,
	ExtendedSupportedTimelineTypes,
	RichContentViewModel,
	TimelineEventTypes,
	UserSessionContext,
} from '../../../../viewmodels/AppViewModels';
import { bs } from '../../../styles/styles';
import { ISelectOption, MultiSelect } from '../../DeprecatedSelect';
import { EntityOpportunities } from '../../dataBoards/opportunities/EntityOpportunities';
import { EntityRichContent } from '../../entities/EntityRichContent';
import { CompanyTimeline } from '../../entities/EntityTimeline/CompanyTimeline';
import { ITimelineFilterOption, TimelineFilter } from '../../entities/EntityTimeline/TimelineFilter';
import { CompanyContactsList } from '../CompanyContactsList';
import { styleSheet } from './styles';

enum CompanyDetailTabs {
	History = 'history',
	Notes = 'notes',
	Contacts = 'contacts',
	Opportunities = 'opportunities',
}

interface IProps {
	company: CompanyViewModel;
	userSession?: UserSessionContext;
}

interface IState {
	company?: CompanyViewModel;
	richContentSupportedTypes?: RichContentSupportedTypes[];
	timelineEventTypes?: TimelineEventTypes[];
	timelineFilterIds?: string[];
}

const richContentStorageKey = 'notesActionsFilterOptions';
const timelineStorageKey = 'timelineFilterOptions';
class _CompanyActivity extends React.Component<IProps, IState> {
	public readonly state: IState = {
		company: this.props.company,
	};

	public UNSAFE_componentWillReceiveProps(nextProps: IProps) {
		if (
			this.props.company !== nextProps.company &&
			!!this.props.company &&
			!!nextProps.company &&
			this.props.company.id !== nextProps.company.id
		) {
			this.setState({
				company: nextProps.company,
			});
			nextProps.company.getRichContent();
		}
	}

	public componentDidMount() {
		this.getRichContentSupportedTypes();
		this.getTimelineSupportedTypes();
		this.setState({ timelineFilterIds: this.getSelectedTimelineFilterIds() });
	}

	public render() {
		const { company, userSession } = this.props;
		const showTimeline = !!userSession.account.features?.timeline?.enabled;
		const { richContentSupportedTypes, timelineEventTypes, timelineFilterIds } = this.state;

		return (
			<Tabs.Root
				defaultValue={showTimeline ? CompanyDetailTabs.History : CompanyDetailTabs.Notes}
				className={css(styleSheet.tabContainer)}
			>
				<Tabs.List className={css(styleSheet.tabList)}>
					{showTimeline ? (
						<Tabs.Trigger className={css(styleSheet.tabTrigger)} value={CompanyDetailTabs.History}>
							History
						</Tabs.Trigger>
					) : (
						<Tabs.Trigger className={css(styleSheet.tabTrigger)} value={CompanyDetailTabs.Notes}>
							Notes / Actions
						</Tabs.Trigger>
					)}
					<Tabs.Trigger className={css(styleSheet.tabTrigger)} value={CompanyDetailTabs.Contacts}>
						Contacts
					</Tabs.Trigger>
					{userSession.account?.features?.boards?.opportunitiesEnabled ? (
						<Tabs.Trigger className={css(styleSheet.tabTrigger)} value={CompanyDetailTabs.Opportunities}>
							Opportunities
						</Tabs.Trigger>
					) : null}
				</Tabs.List>
				{showTimeline ? (
					<Tabs.Content value={CompanyDetailTabs.History} className={css(styleSheet.tabContent)}>
						<div className={css(styleSheet.timelineFilterOptionsContainer)}>
							<TimelineFilter
								options={this.getTimelineFilterOptions()}
								selectedIds={timelineFilterIds ?? []}
								onCommit={this.onTimelineFilterCommit}
							/>
						</div>
						<CompanyTimeline
							companyId={company.id}
							onDeleteRichContent={(richContent: RichContentViewModel) => company.deleteRichContent(richContent)}
							eventTypes={timelineEventTypes}
						/>
					</Tabs.Content>
				) : (
					<Tabs.Content value={CompanyDetailTabs.Notes} className={css(styleSheet.tabContent)}>
						<div className={css(styleSheet.richContentFilterOptionsContainer)}>
							<MultiSelect
								onClose={this.onRichContentFilterClose}
								options={this.getRichContentFilterOptions()}
								dropdownStyles={[styleSheet.dropdown]}
								selectedOptions={this.getDefaultRichContentFilterOptions()}
								selectAllIfNoneSelected={true}
								selectedOptionsTitle={this.getFilterOptionsTitle}
							/>
						</div>
						<EntityRichContent entity={company} supportedTypes={richContentSupportedTypes} />
					</Tabs.Content>
				)}
				<Tabs.Content value={CompanyDetailTabs.Contacts} className={css(styleSheet.tabContent)}>
					<CompanyContactsList company={company} />
				</Tabs.Content>
				{userSession.account?.features?.boards?.opportunitiesEnabled ? (
					<Tabs.Content value={CompanyDetailTabs.Opportunities} className={css(styleSheet.tabContent)}>
						<React.Suspense fallback={<></>}>
							<EntityOpportunities entity={company} isContactMode={false} styles={[bs.overflowAuto]} />
						</React.Suspense>
					</Tabs.Content>
				) : null}
			</Tabs.Root>
		);
	}

	private getDefaultRichContentFilterOptions = (): ISelectOption<INotesActionsCriteria>[] => {
		const notesActionsFilterOptions = window.localStorage.getItem(richContentStorageKey)?.split('|');
		return this.getRichContentFilterOptions().filter(option => {
			if (!notesActionsFilterOptions || notesActionsFilterOptions.length === 0) {
				return option;
			}

			return notesActionsFilterOptions.includes(option.dataContext.type);
		});
	};

	private getSelectedTimelineFilterIds = (): string[] => {
		const timelineFilterOptions = window.localStorage.getItem(timelineStorageKey)?.split('|').filter(Boolean);
		const options = this.getTimelineFilterOptions();

		if (!timelineFilterOptions?.length) {
			return options.map(option => option.id);
		}

		const selectedIds = options
			.filter(option => timelineFilterOptions.includes(option.types.join(',')))
			.map(option => option.id);
		// Matches the timeline's own empty-means-all fallback, so the checkboxes can never disagree with it.
		return selectedIds.length > 0 ? selectedIds : options.map(option => option.id);
	};

	private getRichContentFilterOptions = (): ISelectOption<INotesActionsCriteria>[] => {
		return [
			{
				dataContext: {
					name: 'all',
					type: 'All',
				},
				forceSelectAll: true,
				id: 'rich-content-filter-option-show-all',
				text: 'Show all',
				type: 'checkbox',
			},
			{
				dataContext: {
					name: 'notes',
					type: 'Note',
				},
				id: 'rich-content-filter-option-show-notes',
				text: 'Show notes',
				type: 'checkbox',
			},
			{
				dataContext: {
					name: 'action items',
					type: 'ActionItem',
				},
				id: 'rich-content-filter-option-show-action-items',
				text: 'Show action items',
				type: 'checkbox',
			},
			{
				dataContext: {
					name: 'emails',
					type: 'EmailNote',
				},
				id: 'rich-content-filter-option-show-emails',
				text: 'Show emails',
				type: 'checkbox',
			},
		];
	};

	private getTimelineFilterOptions = (): ITimelineFilterOption[] => {
		const { userSession } = this.props;

		const isCoffeeUser = userSession.account.features.aida.enabled;

		return [
			{ id: 'notes', name: 'notes', label: 'Show notes', types: ['NoteEvent'] },
			{ id: 'action-items', name: 'action items', label: 'Show action items', types: ['ActionItemEvent'] },
			{ id: 'deals', name: 'deals', label: 'Show deals', types: ['DealCreatedEvent', 'DealUpdatedEvent'] },
			{ id: 'emails', name: 'emails', label: 'Show emails', types: ['SentEmailEvent'] },
			{ id: 'texts', name: 'conversations', label: 'Show texts', types: ['ConversationThreadEvent'] },
			{ id: 'meetings', name: 'meetings', label: 'Show meetings', types: ['MeetingEvent'] },
			{
				id: 'phone-calls',
				name: 'phone calls',
				label: 'Show phone calls',
				types: isCoffeeUser
					? ['PhoneCallEvent', 'PhoneCallCompletedEvent', 'UntrackedPhoneCallEvent']
					: ['PhoneCallEvent'],
			},
		];
	};

	private getFilterOptionsTitle(selectedOptions: ISelectOption<INotesActionsCriteria>[]) {
		if (selectedOptions.find(option => option.dataContext.type === 'All')) {
			return <div>Show all</div>;
		}

		const maxCharCount = 25;
		const text = `Show ${selectedOptions.map(option => option.dataContext.name).join(' and ') || 'none'}`;
		return <div>{text.length > maxCharCount ? `${text.slice(0, maxCharCount)}...` : text}</div>;
	}

	private getRichContentSupportedTypes = () => {
		const notesActionsFilterOptions = window.localStorage.getItem(richContentStorageKey)?.split('|');
		const richContentSupportedTypes =
			!notesActionsFilterOptions || notesActionsFilterOptions.length === 0 || notesActionsFilterOptions.includes('All')
				? ['ActionItem', 'Note', 'EmailNote']
				: notesActionsFilterOptions.filter(option => option !== 'All');

		this.setState({
			richContentSupportedTypes: richContentSupportedTypes as RichContentSupportedTypes[],
		});
	};

	private getTimelineSupportedTypes = () => {
		// `filter(Boolean)` drops the empty segment a blank stored value splits into: passing '' as a type
		// would otherwise reach the API, and an empty list makes the server return every event type.
		const timelineFilterOptions: string[] = window.localStorage.getItem(timelineStorageKey)?.split('|').filter(Boolean);
		const timelineEventTypes =
			!timelineFilterOptions || timelineFilterOptions.length === 0 || timelineFilterOptions.includes('All')
				? ExtendedSupportedTimelineTypes
				: timelineFilterOptions?.map(item => item?.split(',') || item)?.filter(option => option !== 'All');

		this.setState({
			timelineEventTypes: timelineEventTypes.flat() as TimelineEventTypes[],
		});
	};

	private onRichContentFilterClose = (selectedOptions: ISelectOption<INotesActionsCriteria>[]) => {
		const selected = selectedOptions.map(option => option.dataContext.type);
		window.localStorage.setItem(richContentStorageKey, selected.join('|'));
		this.getRichContentSupportedTypes();
	};

	private onTimelineFilterCommit = (selectedIds: string[]) => {
		const selected = this.getTimelineFilterOptions()
			.filter(option => selectedIds.includes(option.id))
			.map(option => option.types.join(','));

		try {
			window.localStorage.setItem(timelineStorageKey, selected.join('|'));
		} catch {
			// Storage unavailable (private mode / quota) — the filter still applies for this session.
		}
		this.setState({ timelineFilterIds: selectedIds });
		this.getTimelineSupportedTypes();
	};
}

export const CompanyActivity = observer(_CompanyActivity);
